import { createContext, useEffect, useRef, useState } from 'react';

import { createStore } from 'jotai';
import { useDisclosure } from 'ui/Hooks';

type EditorContextType = {
  store?: ReturnType<typeof createStore>;
  onDirty?: (status: boolean) => void;
  isEditorLoading?: boolean;
  editorLoadingHandler?: ReturnType<typeof useDisclosure>['1'];
  key?: number;
  setKey?: React.Dispatch<React.SetStateAction<number>>;
  headerRef?: React.MutableRefObject<any>;
  resetPreviewRef?: React.MutableRefObject<any>;
  isEditorReadOnly?: boolean;
  setIsEditorReadOnly?: React.Dispatch<React.SetStateAction<boolean>>;
  saveEditorRef?: React.MutableRefObject<any>;
};

const EditorContext = createContext<EditorContextType>({});

const EditorProvider = ({
  onDirty,
  children,
  isLoading = false,
  isReadOnly = false,
  headerRef,
}: {
  onDirty?: (status: boolean) => void;
  children: React.ReactNode;
  isLoading?: boolean;
  isReadOnly?: boolean;
  headerRef?: React.MutableRefObject<any>;
}) => {
  const [key, setKey] = useState(0);
  const [store] = useState(() => createStore());
  const [isEditorLoading, editorLoadingHandler] = useDisclosure(isLoading);
  const resetPreviewRef = useRef<null | (() => void)>(null);
  const saveEditorRef = useRef<null | (() => void)>(null);

  const [isEditorReadOnly, setIsEditorReadOnly] = useState(isReadOnly);

  useEffect(() => {
    if (isLoading) {
      editorLoadingHandler.open();
    } else {
      editorLoadingHandler.close();
    }
  }, [editorLoadingHandler, isLoading]);

  useEffect(() => {
    setIsEditorReadOnly(isReadOnly);
  }, [isReadOnly]);

  return (
    <EditorContext.Provider
      value={{
        store,
        onDirty,
        isEditorLoading,
        isEditorReadOnly,
        setIsEditorReadOnly,
        editorLoadingHandler,
        key,
        setKey,
        headerRef,
        resetPreviewRef,
        saveEditorRef,
      }}
    >
      {children}
    </EditorContext.Provider>
  );
};

export { EditorContext, EditorProvider };
